import { ChevronDown } from 'lucide-react';
import styled from 'styled-components';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-left: auto;
  margin-right: auto;
  max-width: 768px;
`;

const Chevron = styled(ChevronDown)`
  color: var(--muted-foreground);
  flex-shrink: 0;
  transition: transform 200ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Item = styled.details`
  background-color: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  color: var(--card-foreground);
  overflow: hidden;

  &[open] ${Chevron} {
    transform: rotate(180deg);
  }
`;

const Question = styled.summary`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-size: 16px;
  font-weight: 600;
  gap: 16px;
  justify-content: space-between;
  line-height: 24px;
  list-style: none;
  padding: 16px 20px;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    color: var(--primary);
  }

  &:focus-visible {
    border-radius: var(--radius-xl);
    outline: 2px solid var(--ring);
    outline-offset: -2px;
  }
`;

const Answer = styled.p`
  color: var(--muted-foreground);
  font-size: 16px;
  line-height: 24px;
  margin: 0;
  padding: 0 20px 20px;
  text-wrap: pretty;
`;

/**
 * Questions and answers as native <details>, so every answer is in the HTML
 * whether or not it is open.
 *
 *   <FaqList faqs={[{ question, answer }]} />
 *
 * Pair it with <FAQSchema faqs={faqs} /> (json-ld.js) on indexed pages; the
 * same plain-text `answer` feeds both.
 */
const FaqList = ({ className, faqs = [] }) => (
  <List className={className}>
    {faqs.map((faq) => (
      <Item key={faq.question}>
        <Question>
          {faq.question}
          <Chevron aria-hidden="true" size={20} strokeWidth={1.75} />
        </Question>
        <Answer>{faq.answer}</Answer>
      </Item>
    ))}
  </List>
);

export default FaqList;
